import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge, { StatusBadge } from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Tabs from '../../components/ui/Tabs';
import { BULK_CATEGORY_TABS, BULK_CATEGORY_HINTS } from '../../utils/bulkRequestCategories';
import Select from '../../components/ui/Select';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { listBulkRequestTypes, createBulkRequestType, updateBulkRequestType, deleteBulkRequestType } from '../../api/masters';
import { listWorkflowCategories } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

const CANDIDATE_STATUS_OPTIONS = [
  { value: '', label: 'No status change' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'hold', label: 'Hold' },
];

// Step definitions across every workflow, grouped by step group. Linking a definition covers
// that step in all the workflows it appears in.
function useStepGroups(enabled) {
  const { data } = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories, enabled });
  return useMemo(() => {
    const defs = new Map();
    (data || []).forEach((c) => {
      const groupNames = new Map((c.groups || []).map((g) => [g.id, g.name]));
      (c.steps || []).forEach((st) => {
        const entry = defs.get(st.step_definition_id) || {
          id: st.step_definition_id,
          title: st.title,
          group: groupNames.get(st.step_group_id) || 'Ungrouped',
          order: st.sort_order,
          workflows: 0,
        };
        entry.workflows += 1;
        defs.set(st.step_definition_id, entry);
      });
    });
    const groups = new Map();
    [...defs.values()]
      .sort((a, b) => a.order - b.order)
      .forEach((d) => groups.set(d.group, [...(groups.get(d.group) || []), d]));
    return [...groups.entries()];
  }, [data]);
}

function TypeFormModal({ open, onClose, type, category }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [candidateStatus, setCandidateStatus] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');
  const [stepIds, setStepIds] = useState([]);
  const isDisplay = (type?.category || category) === 'display';
  const stepGroups = useStepGroups(isDisplay);

  useEffect(() => {
    setStepIds(type?.step_definition_ids || []);
    setName(type?.name || '');
    setCandidateStatus(type?.candidate_status || '');
    setIsActive(type?.is_active ?? true);
    setError('');
  }, [type]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        category: type?.category || category,
        candidate_status: (type?.category || category) === 'action' ? candidateStatus || null : null,
        is_active: isActive,
        ...(isDisplay && { step_definition_ids: stepIds }),
      };
      return type ? updateBulkRequestType(type.id, payload) : createBulkRequestType(payload);
    },
    onSuccess: () => {
      pushToast({
        type: 'success',
        message: type ? 'Type updated.' : 'Type created.',
      });
      queryClient.invalidateQueries({ queryKey: ['bulk-request-types'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save type.'),
  });

  const handleSave = () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={type ? 'Edit bulk request type' : 'New bulk request type'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        {(type?.category || category) === 'action' && (
          <>
            <Select label="Candidate status" value={candidateStatus} onChange={(e) => setCandidateStatus(e.target.value)}>
              {CANDIDATE_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <p className="-mt-2 text-xs text-gray-500">
              Applied to every candidate in a request of this type when it is approved. Suspended candidates are hidden from lists and
              dashboards; on-hold candidates stay visible but frozen.
            </p>
          </>
        )}
        {(type?.category || category) !== 'action' && (
          <p className="text-xs text-gray-500">{BULK_CATEGORY_HINTS[type?.category || category]}</p>
        )}
        {isDisplay && (
          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">Show on workflow steps</p>
            <p className="mb-2 text-xs text-gray-500">
              Requests of this type appear (pending and approved only) on the selected steps in every workflow that has them. Leave empty
              to show only on the step the candidate was at when the request was raised.
            </p>
            <div className="max-h-64 space-y-3 overflow-y-auto rounded-md border border-gray-200 p-3">
              {stepGroups.map(([group, defs]) => (
                <div key={group}>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{group}</div>
                  {defs.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 py-0.5 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={stepIds.includes(d.id)}
                        onChange={() => setStepIds((prev) => (prev.includes(d.id) ? prev.filter((x) => x !== d.id) : [...prev, d.id]))}
                      />
                      {d.title}
                      <span className="text-xs text-gray-400">({d.workflows} workflow{d.workflows === 1 ? '' : 's'})</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active (offered when raising a bulk request)
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function BulkRequestTypeMasters() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [tab, setTab] = useState('action');

  const typesQuery = useQuery({
    queryKey: ['bulk-request-types', { include_inactive: true }],
    queryFn: () => listBulkRequestTypes({ include_inactive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBulkRequestType,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Type deleted.' });
      queryClient.invalidateQueries({ queryKey: ['bulk-request-types'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err.response?.data?.message || err.response?.data?.errors?.name?.[0] || 'Could not delete type.',
      });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Name' },
    ...(tab === 'display'
      ? [{ key: 'steps', label: 'Steps', render: (row) => `${row.step_definition_ids?.length || 0} linked` }]
      : []),
    {
      key: 'candidate_status',
      label: 'Candidate status',
      render: (row) => (row.candidate_status ? <StatusBadge status={row.candidate_status} /> : <span className="text-gray-400">—</span>),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="masters.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Bulk Request Types"
        description="Request types staff pick from when raising a bulk request against multiple candidates."
        actions={
          <Can perm="masters.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Type
            </Button>
          </Can>
        }
      />

      <Tabs tabs={BULK_CATEGORY_TABS} active={tab} onChange={setTab} />
      <p className="mb-4 mt-3 text-sm text-gray-500">{BULK_CATEGORY_HINTS[tab]}</p>

      <Table
        columns={columns}
        data={(typesQuery.data || []).filter((t) => (t.category || 'action') === tab)}
        isLoading={typesQuery.isLoading}
        emptyMessage="No bulk request types yet."
      />

      {formOpen && <TypeFormModal open={formOpen} onClose={() => setFormOpen(false)} type={formTarget} category={tab} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete bulk request type"
        message={`Delete "${deleteTarget?.name}"? This only works if no bulk request has used it yet.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
